import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { blockEdges, snapFoldHeight } from './fold-point';
import styles from './MessageBubble.module.css';

/** An earlier answer folds once it is taller than this. */
const ROLLUP_MAX_REM = 16;
/** Folding saves little on an answer barely past the limit, so only answers
 *  this much taller than it fold. */
const ROLLUP_SLACK = 1.4;

interface RollupBodyProps {
  /** False for live replies (everything since the user's latest question)
   *  and anything still streaming: the reply the user is reading, and a
   *  card whose buttons they are still using, are never folded. */
  readonly enabled: boolean;
  readonly children: ReactNode;
  /** Renders the row under the answer, given the fold toggle (or null when
   *  the answer does not fold), so the toggle shares that row rather than
   *  adding one of its own. */
  readonly renderFooter: (foldToggle: ReactNode) => ReactNode;
}

/**
 * Folds a long, older answer to a short preview that fades out, with a
 * control to unfold it. Keeps the conversation scannable in a narrow panel:
 * the latest turn takes the space, earlier turns read as a list of
 * question-and-gist pairs.
 */
export function RollupBody({
  enabled,
  children,
  renderFooter,
}: RollupBodyProps) {
  // The answer is measured on an inner wrapper the fold never clamps: the
  // clamped box does not resize when the content grows (a chart loading, the
  // panel narrowing), so observing it would leave the fold at a stale height
  // and cut the answer off even when expanded.
  const innerRef = useRef<HTMLDivElement>(null);
  const [fullHeight, setFullHeight] = useState(0);
  const [foldHeight, setFoldHeight] = useState(0);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!enabled || !el || typeof ResizeObserver === 'undefined') {
      setIsOverflowing(false);
      return;
    }
    const measure = () => {
      const rootFontPx = Number.parseFloat(
        getComputedStyle(document.documentElement).fontSize
      );
      const foldPx = ROLLUP_MAX_REM * rootFontPx;
      const height = el.offsetHeight;
      const overflowing = height > foldPx * ROLLUP_SLACK;
      setFullHeight(height);
      setIsOverflowing(overflowing);
      // Fold between blocks, never through a card (OMNI-1218).
      if (overflowing) {
        setFoldHeight(snapFoldHeight(blockEdges(el, foldPx), foldPx));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled]);

  const canFold = enabled && isOverflowing;
  const isFolded = canFold && !isExpanded;

  return (
    <div className={styles.rollup} data-folded={isFolded}>
      <div
        className={styles.rollupContent}
        style={
          canFold
            ? ({
                '--rollup-full': `${fullHeight}px`,
                // Measured with isOverflowing, so set whenever canFold is.
                '--rollup-max': `${foldHeight}px`,
              } as CSSProperties)
            : undefined
        }
        data-can-fold={canFold}
      >
        <div ref={innerRef}>{children}</div>
      </div>
      {renderFooter(
        canFold ? (
          <button
            type="button"
            className={styles.rollupToggle}
            aria-expanded={isExpanded}
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded ? 'Show less' : 'Show full answer'}
          </button>
        ) : null
      )}
    </div>
  );
}
